<template>
  <list-page-shell>
    <template #actions>
      <el-button v-if="canFinanceRecalc" type="primary" size="small" :loading="batchLoading" @click="recalcMonth">
        {{ $t('leadCostMonthPage.recalcMonth') }}
      </el-button>
      <el-button size="small" icon="el-icon-refresh" @click="load">{{ $t('common.refresh') }}</el-button>
      <el-button size="small" @click="$router.push('/project/list')">{{ $t('leadCostMonthPage.goProjectList') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small">
        <el-form-item :label="$t('leadCostMonthPage.periodMonth')">
          <el-input v-model="periodMonth" placeholder="yyyy-MM" style="width: 120px;" clearable @change="onMonthChange" />
        </el-form-item>
      </el-form>
    </template>

    <p class="hint">{{ $t('leadCostMonthPage.subtitle') }}</p>

    <el-table v-loading="loading" :data="rows" size="small" border>
      <el-table-column :label="$t('leadCostMonthPage.colLead')" min-width="200">
        <template slot-scope="scope">
          <router-link v-if="scope.row.lead && scope.row.lead.id" class="link" :to="'/lead/detail/' + scope.row.lead.id">
            {{ leadLabel(scope.row.lead) }}
          </router-link>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('leadCostMonthPage.colOwner')" min-width="120" show-overflow-tooltip>
        <template slot-scope="scope">{{ ownerDisplay(scope.row) }}</template>
      </el-table-column>
      <el-table-column :label="$t('leadCostMonthPage.colMonthlySales')" width="130" align="right">
        <template slot-scope="scope">{{ formatUsd(scope.row.monthlySalesAmount) }}</template>
      </el-table-column>
      <el-table-column :label="$t('leadCostMonthPage.colCost')" width="130" align="right">
        <template slot-scope="scope">{{ formatUsd(scope.row.leadCost && scope.row.leadCost.costAmount) }}</template>
      </el-table-column>
      <el-table-column :label="$t('leadCostMonthPage.colRatio')" width="100" align="right">
        <template slot-scope="scope">{{ formatRatio(scope.row.leadCost && scope.row.leadCost.costSourceRatio) }}</template>
      </el-table-column>
      <el-table-column :label="$t('leadCostMonthPage.colComputed')" min-width="160">
        <template slot-scope="scope">{{ (scope.row.leadCost && scope.row.leadCost.computedAt) || '—' }}</template>
      </el-table-column>
      <el-table-column v-if="canFinanceRecalc" :label="$t('common.actions')" width="110" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" :loading="scope.row._loading" @click="recalcRow(scope.row)">
            {{ $t('leadCostMonthPage.recalcRow') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50]"
        @current-change="load"
        @size-change="onSizeChange"
      />
    </div>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import { formatUsd } from '@/utils/formatUsd'
import { fetchLeadCostMonthPage, recalcLeadCost, recalcLeadCostMonth } from '@/api/finance'

export default {
  name: 'LeadCostMonth',
  components: { ListPageShell },
  data() {
    const d = new Date()
    const pm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    return {
      periodMonth: pm,
      loading: false,
      batchLoading: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 10
    }
  },
  created() {
    this.load()
  },
  computed: {
    canFinanceRecalc() {
      const r = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return r.includes('OFFICE') || r.includes('ADMIN') || r.includes('SALES')
    }
  },
  methods: {
    formatUsd(v) {
      if (v == null || v === '') return '—'
      return formatUsd(v)
    },
    formatRatio(r) {
      if (r == null || r === '') return '—'
      const n = Number(r)
      if (Number.isNaN(n)) return String(r)
      return `${Math.round(n * 100)}%`
    },
    ownerDisplay(row) {
      const name = row && row.assignedSalesUserDisplayName != null ? String(row.assignedSalesUserDisplayName).trim() : ''
      if (name) return name
      const id = row && row.lead && row.lead.assignedSalesUserId
      if (id != null && id !== '') return `#${id}`
      return '—'
    },
    leadLabel(lead) {
      if (!lead) return '—'
      const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim()
      if (name) return name
      if (lead.leadNo) return lead.leadNo
      return `#${lead.id}`
    },
    onMonthChange() {
      this.page = 1
      this.load()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const resp = await fetchLeadCostMonthPage({
          periodMonth: (this.periodMonth || '').trim() || undefined,
          page: this.page,
          size: this.pageSize
        })
        if (resp && resp.code != null && resp.code !== 200) {
          this.$message.error((resp.msg || resp.message) || this.$t('common.failed'))
          return
        }
        const data = resp.data || {}
        this.rows = (data.records || []).map(r => ({ ...r, _loading: false }))
        this.total = data.total != null ? data.total : 0
        if (data.periodMonth) {
          this.periodMonth = data.periodMonth
        }
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.loading = false
      }
    },
    async recalcMonth() {
      this.batchLoading = true
      try {
        const resp = await recalcLeadCostMonth((this.periodMonth || '').trim() || undefined)
        if (resp && resp.code != null && resp.code !== 200) {
          this.$message.error((resp.msg || resp.message) || this.$t('common.failed'))
          return
        }
        const inner = resp.data || {}
        const n = inner.recalculatedCount != null ? inner.recalculatedCount : 0
        this.$message.success(`${this.$t('leadCostMonthPage.doneBatch')} (${n})`)
        await this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.batchLoading = false
      }
    },
    async recalcRow(row) {
      if (!row.lead || !row.lead.id) return
      this.$set(row, '_loading', true)
      try {
        const resp = await recalcLeadCost(row.lead.id, (this.periodMonth || '').trim() || undefined)
        if (resp && resp.code != null && resp.code !== 200) {
          this.$message.error((resp.msg || resp.message) || this.$t('common.failed'))
          return
        }
        this.$message.success(this.$t('leadDetailPage.leadCostOk'))
        await this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.$set(row, '_loading', false)
      }
    }
  }
}
</script>

<style scoped>
.hint {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--dark-text-sub, #8b95a1);
  line-height: 1.5;
}
.link {
  color: #7dd3fc;
  text-decoration: none;
}
.link:hover {
  text-decoration: underline;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>
